/**
 * Backfill supplier contact snapshots on legacy supermarket invoices.
 */

import invoiceService from '../services/invoiceService';
import { findSupplierById } from './resolveInvoiceSupplier';

function needsSupplierContactEnrichment(invoice) {
  if (!invoice || typeof invoice !== 'object') return false;
  const missingAddress = !String(invoice.supplierAddress || '').trim();
  const missingPhone = !String(invoice.supplierPhone || '').trim();
  const missingName = !String(invoice.supplierName || '').trim();
  return missingAddress || missingPhone || missingName;
}

function enrichInvoiceSupplierContact(invoice, suppliers) {
  if (!invoice || !needsSupplierContactEnrichment(invoice)) {
    return { invoice, changed: false };
  }

  const found = findSupplierById(suppliers, invoice.supplierId);
  if (!found && !invoice.supplierName) {
    return { invoice, changed: false };
  }

  const next = { ...invoice };
  let changed = false;

  if (!String(next.supplierName || '').trim()) {
    const name = found?.name || found?.supplierName || '';
    if (name) {
      next.supplierName = name;
      changed = true;
    }
  }

  if (!String(next.supplierAddress || '').trim()) {
    const address = found?.address || '';
    if (address) {
      next.supplierAddress = address;
      changed = true;
    }
  }

  if (!String(next.supplierPhone || '').trim()) {
    const phone = found?.phone || '';
    if (phone) {
      next.supplierPhone = phone;
      changed = true;
    }
  }

  return { invoice: changed ? next : invoice, changed };
}

/**
 * @param {object[]} invoices
 * @param {object[]} suppliers
 * @returns {{ invoices: object[], changedCount: number }}
 */
export function enrichSupermarketInvoicesSupplierContact(invoices, suppliers = []) {
  if (!Array.isArray(invoices) || invoices.length === 0) {
    return { invoices: invoices || [], changedCount: 0 };
  }

  let changedCount = 0;
  const enriched = invoices.map((invoice) => {
    const { invoice: next, changed } = enrichInvoiceSupplierContact(invoice, suppliers);
    if (changed) changedCount += 1;
    return next;
  });

  return { invoices: enriched, changedCount };
}

/**
 * Enrich + persist when legacy invoices lack supplier contact snapshots.
 * @param {string} businessId
 * @param {object[]} invoices
 * @param {object[]} suppliers
 * @returns {Promise<{ invoices: object[], changedCount: number, persisted: boolean }>}
 */
export async function applySupermarketInvoiceSupplierContactMigration(
  businessId,
  invoices,
  suppliers = []
) {
  const consolidated = invoiceService.consolidateInvoicesInMemory(
    Array.isArray(invoices) ? invoices : []
  );
  const { invoices: enriched, changedCount } = enrichSupermarketInvoicesSupplierContact(
    consolidated,
    suppliers
  );

  if (!businessId || changedCount === 0) {
    return { invoices: enriched, changedCount, persisted: false };
  }

  await invoiceService.persistSupermarketInvoices(enriched, businessId);
  return { invoices: enriched, changedCount, persisted: true };
}
